import { useCallback } from "react";
import { isValidEmail } from "@/lib/stringUtil";
import { useDispatch, useSelector } from "react-redux";
import { postMessage } from "@/store/contactPremiumSlice";
import { AppDispatch, RootState } from "@/store";
import useFormValidation, { ValidationRules } from "@/hooks/useFormValidation";

export function useContactForm() {
  const dispatch = useDispatch<AppDispatch>();
  const { loading: toastLoading } = useSelector(
    (state: RootState) => state.contactPremium
  );

  // Initial form values
  const initialValues = {
    fromNameFirst: "",
    fromNameLast: "",
    fromEmail: "",
    confirmEmail: "",
    subject: "",
    message: "",
  };

  // Validation rules using the format expected by useFormValidation
  const validationRules: ValidationRules = {
    fromNameFirst: {
      required: true,
      requiredMessage: "First Name is required and cannot be blank.",
    },
    fromNameLast: {
      required: true,
      requiredMessage: "Last Name is required and cannot be blank.",
    },
    fromEmail: {
      required: true,
      requiredMessage: "Email is required and cannot be blank.",
      validator: (value: string) => isValidEmail(value),
      errorMessage: "Please enter a valid email address.",
    },
    confirmEmail: {
      required: true,
      requiredMessage: "Confirm Email is required and cannot be blank.",
      validator: (value: string, allValues: Record<string, any>) =>
        isValidEmail(value) && value === allValues.fromEmail,
      errorMessage: "The email addresses do not match.",
    },
    subject: {
      required: true,
      requiredMessage: "Subject is required and cannot be blank.",
    },
    message: {
      required: true,
      requiredMessage: "Message is required and cannot be blank.",
    },
  };

  // Use the form validation hook
  const {
    values: formValues,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    isFormValid,
    resetForm,
  } = useFormValidation(initialValues, validationRules);

  // Create a complete form data object with default fields
  const formData = {
    ...formValues,
    origin: "EXTERNAL",
    type: "EMAIL",
  };

  // Handle form field changes
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      handleInputChange(name, value);
    },
    [handleInputChange]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (isFormValid()) {
        try {
          await dispatch(
            postMessage({
              ...formData,
              origin: "EXTERNAL",
              type: "EMAIL",
            })
          ).unwrap();
          resetForm(); // Reset form after successful submission
        } catch (error) {
          console.error("Error submitting form:", error);
        }
      } else {
        validateForm(); // This will mark all fields as touched and show errors
      }
    },
    [dispatch, formData, isFormValid, validateForm, resetForm]
  );

  // Clear all form fields
  const handleClearAll = useCallback(() => {
    resetForm();
  }, [resetForm]);

  return {
    formData,
    errors,
    touched,
    toastLoading,
    isFormValid,
    handleChange,
    handleBlur,
    handleSubmit,
    handleClearAll,
  };
}
